// src/pages/ParentPanel.tsx
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { api } from '../api/client'
import { useApp } from '../lib/store'
import type { Hunt } from '../types'

export function ParentPanel() {
  const navigate = useNavigate()
  const { state } = useApp()
  const [hunts, setHunts] = useState<(Hunt & { egg_count: number })[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadHunts()
  }, [])

  const loadHunts = async () => {
    try {
      const result = await api.hunts.list()
      setHunts(result.hunts)
    } catch {
      // Handle error silently for now
    } finally {
      setLoading(false)
    }
  }

  const statusLabels: Record<string, { text: string; color: string }> = {
    draft: { text: 'Utkast', color: 'text-sunshine' },
    active: { text: 'Aktiv', color: 'text-mint' },
    completed: { text: 'Klar', color: 'text-ink/40' },
  }

  const handleHuntClick = (hunt: Hunt) => {
    if (state.role === 'child' && hunt.status === 'active') {
      navigate(`/play/${hunt.id}`)
    }
  }

  return (
    <div className="min-h-dvh p-6 bg-cream">
      <div className="max-w-lg mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="font-heading text-2xl text-ink">
            {state.role === 'parent' ? 'Dina jakter' : 'Välj jakt'}
          </h1>
          <Button variant="ghost" size="sm" onClick={() => navigate('/role')}>
            ←
          </Button>
        </div>

        {state.role === 'parent' && (
          <Button
            size="lg"
            className="w-full mb-6"
            onClick={() => navigate('/parent/create')}
          >
            + Skapa ny jakt
          </Button>
        )}

        {loading ? (
          <p className="text-center text-ink/50 font-body">Laddar...</p>
        ) : hunts.length === 0 ? (
          <p className="text-center text-ink/50 font-body mt-8">
            Inga jakter ännu. Skapa din första!
          </p>
        ) : (
          <AnimatePresence>
            <div className="flex flex-col gap-3">
              {hunts.map((hunt, i) => (
                <motion.div
                  key={hunt.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Card
                    onClick={() => {
                      if (state.role === 'parent') {
                        navigate(`/parent/edit/${hunt.id}`)
                      } else if (hunt.status === 'active') {
                        navigate(`/play/${hunt.id}`)
                      }
                    }}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-heading text-lg text-ink">{hunt.name}</h3>
                        <p className="font-body text-base text-ink/50">
                          {hunt.egg_count} ägg
                        </p>
                      </div>
                      <span className={`font-heading text-sm ${statusLabels[hunt.status]?.color}`}>
                        {statusLabels[hunt.status]?.text}
                      </span>
                    </div>

                    {state.role === 'parent' && (
                      <div className="flex gap-2 mt-3">
                        {hunt.status === 'draft' && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => api.hunts.update(hunt.id, { status: 'active' }).then(loadHunts)}
                          >
                            Aktivera jakt
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (confirm(`Ta bort "${hunt.name}"? Kan inte ångras.`)) {
                              api.hunts.delete(hunt.id).then(loadHunts)
                            }
                          }}
                          className="text-coral/70 hover:text-coral"
                        >
                          Ta bort
                        </Button>
                      </div>
                    )}
                  </Card>
                </motion.div>
              ))}
            </div>
          </AnimatePresence>
        )}
      </div>
    </div>
  )
}
